import { Box, ButtonProps, IconButton, Input } from '@chakra-ui/react';
import * as React from 'react';

import { EPrepVersionFilesSource } from '@/graphql-types/globalTypes';
import { useFileUpload } from '@/hooks/useFileUpload';
import { PaperclipIcon } from '@/imports/ui/chakra/feather';

export const FileUploadButton = React.forwardRef<
  HTMLInputElement,
  {
    value: { versionFileId: string; fileName: string }[];
    onChange: (value: { versionFileId: string; fileName: string }[]) => void;
    onBlur: () => void;
  } & ButtonProps
>(({ value, onChange, onBlur, ...buttonProps }, ref) => {
  const internalRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

  const { uploadFiles } = useFileUpload(EPrepVersionFilesSource.FEED_ITEM);

  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <Box>
      <Input
        type="file"
        multiple={false}
        onChange={async (event) => {
          setIsLoading(true);
          const files = event.currentTarget.files
            ? Array.from(event.currentTarget.files)
            : [];
          const promisedResources = files.map(async (file) => {
            const resource = await uploadFiles([file]);

            return resource
              ? { versionFileId: resource[0], fileName: file.name }
              : null;
          });
          const resources = (await Promise.all(promisedResources)).filter(
            (
              resource
            ): resource is { versionFileId: string; fileName: string } =>
              !!resource
          );

          setIsLoading(false);
          return onChange && onChange([...(value || []), ...resources]);
        }}
        onBlur={onBlur}
        ref={internalRef}
        display="none"
      />
      <IconButton
        aria-label="Upload Files"
        icon={<PaperclipIcon />}
        onClick={() => internalRef.current?.click()}
        isLoading={isLoading}
        {...buttonProps}
      />
    </Box>
  );
});

FileUploadButton.displayName = 'FileUploadButton';
